import React from 'react';
import { Tabs as AntdTabs } from 'antd';
import type { TabsProps } from 'antd';
import { css, Global } from '@emotion/react';
import styled from '@emotion/styled';
import { typeStyle } from 'constants/typography';

/**
 * The design system's Nav / Tab, rendered by every horizontal tab surface including the page bars in
 * `components/common/menu`.
 * https://www.figma.com/design/vhfYlbrrUsQrx6AvWGcE9h/Design-system?node-id=152-21
 *
 * Hover/selected label colours live in `tabsComponent` (antd.constants), not here: antd's ink-bar and
 * active-label selectors outrank anything emotion puts on the root. What is left below is the type
 * ramp, the geometry, and the states antd has no token for.
 */
export const TABS_OVERFLOW_POPUP_CLASS = 'env0-tabs-overflow';

/* antd portals the more-dropdown to document.body, so no rule under the styled root reaches it. A
   forwarded class plus global rules is the fix — reparenting the popup drags the bar off-screen on
   hover. Tabs never hides the active tab, so the popup needs no selected state. */
const overflowPopupStyles = css`
  .${TABS_OVERFLOW_POPUP_CLASS} .ant-tabs-dropdown-menu-item {
    ${typeStyle.labelMd}
    color: var(--text-body);

    &::first-letter {
      text-transform: uppercase;
    }

    &:hover {
      color: var(--text-heading);
      background: var(--surface-secondary-background);
    }
  }
`;

const Tabs: React.FC<TabsProps> = ({ popupClassName, ...props }) => {
  return (
    <>
      <Global styles={overflowPopupStyles} />
      <StyledTabs popupClassName={[TABS_OVERFLOW_POPUP_CLASS, popupClassName].filter(Boolean).join(' ')} {...props} />
    </>
  );
};

const StyledTabs = styled(AntdTabs)`
  .ant-tabs-nav {
    margin: 0;

    &::before {
      border-bottom: var(--border-width-xs) solid var(--border-primary);
    }
  }

  .ant-tabs-tab {
    /* Figma Nav / Tab: "Label 14 Rubik" — the same rung the Menu bar takes. */
    ${typeStyle.labelMd}
    height: 32px;
    padding: 0 var(--padding-xs);

    .ant-tabs-tab-btn {
      color: var(--text-body);

      /* Sentence case, per Nav / Tab (APO-465). ::first-letter and not capitalize, which re-cases
           labels a call site already set. Two selectors because ::first-letter only generates on a
           block container with leading text: the btn covers a plain string, the first-child chain
           reaches a label wrapping a flex row without uppercasing the count badge. */
      &::first-letter {
        text-transform: uppercase;
      }

      *:first-child::first-letter {
        text-transform: uppercase;
      }
    }

    /* The bridge's itemHoverColor lands on the tab, but the default colour above sits on the
         btn and an own colour beats inheritance — so hover is repainted where default is set.
         Measured: without this the label stayed at text-body under the cursor. */
    &:hover .ant-tabs-tab-btn {
      color: var(--text-heading);
    }

    &.ant-tabs-tab-disabled .ant-tabs-tab-btn {
      color: var(--text-disabled);
    }

    + .ant-tabs-tab {
      /* Figma's Tab navigation packs its Nav / Tab items at a 4px gap (267:6869). */
      margin-left: var(--spacing-xxxs);
    }
  }

  /* The "…" trigger is a button outside .ant-tabs-tab, so the ramp and colours above miss it. */
  .ant-tabs-nav-more {
    ${typeStyle.labelMd}
    color: var(--text-body);
    padding: 0 var(--padding-xs);

    &:hover {
      color: var(--text-heading);
    }
  }

  .ant-tabs-ink-bar {
    height: var(--border-width-sm);
    /* Figma rounds the 2px indicator's corners by 1px (152:16). */
    border-radius: var(--scale-25);
  }
`;

export default Tabs;
